import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogClose, DialogTitle } from '@radix-ui/react-dialog';

import { EditIcon } from '@/assets/icons';
import { Button, IconBox, Input, Spinner } from '@/components/atoms';
import { iconByName } from '@/components/atoms/Icon/constants';
import { CATEGORY_POPUP_TEST_IDS } from '@/constants/data-testids';
import { captureAsyncError } from '@/utils/captureAsyncError';

import { CategoryPopupData, SetCategoryPopupData, SetCategoryPopupStep } from '../CategoryPopup';

type ManageCategoryPopupProps = {
  type: 'create' | 'edit';
  categoryData: CategoryPopupData;
  isLoadingTranslateCategory?: boolean;
  setCategoryData: SetCategoryPopupData;
  setStep: SetCategoryPopupStep;
  onNextStepClick: () => Promise<void>;
};

export const ManageCategoryPopup: FC<ManageCategoryPopupProps> = ({
  type,
  categoryData,
  isLoadingTranslateCategory,
  setCategoryData,
  setStep,
  onNextStepClick,
}) => {
  const { icon, name, description } = categoryData;
  const { t } = useTranslation('components');
  const title =
    type === 'create'
      ? t('manageCategoryPopup.buttonNewCategory')
      : t('manageCategoryPopup.buttonEditCategory');

  return (
    <div data-testid={CATEGORY_POPUP_TEST_IDS.STEP1}>
      <div className="mb-5">
        <DialogTitle className="text-body-12-semibold text-custom-warm-grey-500 mb-2">
          {t('manageCategoryPopup.title')}
        </DialogTitle>
        <p className="text-body-20-semibold text-custom-warm-grey-800">{title}</p>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <IconBox
          icon={iconByName[icon ?? 'question']}
          size="large"
          iconClassName="text-custom-cyan-500"
        />
        <Button
          size="xs"
          variant="teritary-bright"
          leftIcon={<EditIcon width={20} height={20} className="text-custom-warm-grey-500" />}
          onClick={() => setStep('step1-icon')}
        >
          {t('manageCategoryPopup.changeButton')}
        </Button>
      </div>

      <div className="mb-6">
        <Input
          placeholder={`${t('manageCategoryPopup.inputs.categoryPlaceholder')}`}
          label={`${t('manageCategoryPopup.inputs.categoryLabel')}`}
          className="w-full mb-5"
          value={name ?? ''}
          onChange={e => setCategoryData(prev => ({ ...prev, name: e.target.value }))}
        />
        <Input
          placeholder={`${t('manageCategoryPopup.inputs.descriptionPlaceholder')}`}
          label={`${t('manageCategoryPopup.inputs.descriptionLabel')}`}
          as="textarea"
          className="w-full"
          value={description ?? ''}
          onChange={e => setCategoryData(prev => ({ ...prev, description: e.target.value }))}
        />
      </div>

      <div className="flex items-center gap-3">
        <DialogClose asChild>
          <Button size="lg" variant="secondary" className="w-full">
            {t('manageCategoryPopup.cancelButton')}
          </Button>
        </DialogClose>
        <Button
          size="lg"
          variant="primary"
          className="relative w-full"
          onClick={() => {
            void Promise.resolve(onNextStepClick()).catch(captureAsyncError);
          }}
        >
          {t('manageCategoryPopup.nextStep')}
          {isLoadingTranslateCategory && (
            <Spinner className="absolute top-1/2 -translate-y-1/2 right-8" />
          )}
        </Button>
      </div>
    </div>
  );
};
